import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Card,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Button
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";
import CustomTable from "components/CustomTable/CustomTable";
import PaginationControls from "components/PaginationControls/PaginationControls";

function Cancel() {
  const [cancelRequests, setCancelRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');

  // Fetch cancel requests with pagination
  const getCancelRequests = useCallback(
    async (page, search) => {
      setLoading(true);
      try {
        const params = { page, total: totalItems, status: status, search, };

        const response = await apiRequest.get(endPoints.GET_CANCEL_REQUESTS, params);

        if (response.success) {
          const { items, pagination } = response.data;
          setCancelRequests(items);
          setTotalPages(pagination.total_pages);
          setCurrentPage(pagination.current_page);
          setTotalItems(pagination.totalItems);
        }
      } catch (error) {
        console.error("Error fetching cancel requests:", error);
        setError("Failed to fetch cancel requests. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [totalItems, status]
  );

  useEffect(() => {
    setAccessToken(localStorage.getItem("access_token"));
    getCancelRequests(currentPage, search);
  }, [currentPage, status, getCancelRequests, search]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const columns = useMemo(() => [
    {
      key: "business_name",
      label: "Business Name",
      render: (_, row) => (
        <MDBox display="flex" alignItems="center">
          <Avatar
            src={`${apiRequest.BASE_URL}/${row.business?.profile}`}
            sx={{ width: 36, height: 36, mr: 1 }}
          />
          <MDTypography variant="button" fontWeight="medium">
            {row.business?.company_name || row.business?.user?.full_name || "N/A"}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      key: "contact",
      label: "Contact",
      render: (_, row) => (
        <MDTypography variant="caption">
          {row.business?.user?.contact || row.business?.contact || "N/A"}
        </MDTypography>
      ),
    },
    {
      key: "reason",
      label: "Cancel Reason",
      render: (reason) => (
        <MDTypography variant="caption" color="text">
          {reason || "No reason provided"}
        </MDTypography>
      ),
    },
    {
      key: "status",
      label: "Status",
    },
  ], []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Cancel Requests
                </MDTypography>
              </MDBox>
              <MDBox pt={3} px={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      variant={status === 'pending' ? 'contained' : 'outlined'}
                      color="info"
                      onClick={() => setStatus('pending')}
                    >
                      Pending
                    </MDButton>
                    <MDButton
                      variant={status === 'approved' ? 'contained' : 'outlined'}
                      color="success"
                      onClick={() => setStatus('approved')}
                    >
                      Approved
                    </MDButton>
                    <MDButton
                      variant={status === 'rejected' ? 'contained' : 'outlined'}
                      color="error"
                      onClick={() => setStatus('rejected')}
                    >
                      Rejected
                    </MDButton>
                  </MDBox>
                  <MDBox display="flex" gap={1}>
                    <TextField
                      label="Search"
                      variant="outlined"
                      size="small"
                      placeholder="Search requests..."
                      sx={{ width: 300 }}
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </MDBox>
                </MDBox>

                <CustomTable
                  data={cancelRequests}
                  columns={columns}
                  onViewDetails={(row) => {
                    alert(`Viewing details for: ${row.business?.company_name || row.business?.user?.full_name || 'Request'}`);
                  }}
                />

                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error/Success Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default Cancel;
